import { Component, Inject, OnInit, TemplateRef } from '@angular/core';
import { AppComponentName, Message, MessageActionType, MessageType, PreEditObject, Sensor, SensorInfo, SensorReadingUpdateCriteria, UserGroup } from '@models';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import { SensorMapping } from 'app/shared/classes/sensorMapping';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';


export interface ImportSensorModalParams {
  allSensors: Sensor[];
  userGroups: UserGroup[];
}


@Component({
  standalone: false,
  selector: 'app-import-sensor-modal',
  templateUrl: './import-sensor-modal.component.html',
  styleUrls: ['./import-sensor-modal.component.scss']
})

export class ImportSensorComponent implements OnInit, ImportSensorModalParams {

  public auth = new Authorization();
  public common = new CommonShared();
  public mapping = new SensorMapping();
  
  modalDragOn: boolean = false;

  public allSensors: Sensor[];
  public userGroups: UserGroup[];

  public username: string;
  public isSensorImportModalReady: boolean = true;
  
  public readyToImportFileContent: boolean = false;
  public csvFilename: string = '';
  public csvFileContent: string[][] = [];
  public importCSVFileAnalysisResults: string[];
  public analyzingCSVFile: boolean = false;
  public importingCSVFileContents: boolean = false;
  public sensorsToImport: Sensor[] = [];
  public sensorsWithDates: number = 0;
  public sensorsWithInvalidDates: number = 0;

  public changeLogNotes = '';
  public preEditObjects: PreEditObject[] = [];
  public preEditObjectAsJSONString = '';

  public validImportColumns = [ 'UUID', 'Name', 'Type', 'UserGroup', 'Notes', 'GPS_Lat', 'GPS_Long', 'GPS_StartDate', 'GPS_EndDate', 'GPS_LocationDescription' ];
  public requiredImportColumns = [ 'UUID' ];

  public globalSwitch: boolean;

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _appMessageService: AppMessageService, private confirmationService: ConfirmationService,
              public _userService: UserService, private global: AppComponent) {
    this.globalSwitch = global.globalSwitch;
  }


  ngOnInit(): void {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      () => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }

  startUp(): void {
  }

  
  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  csvFileSelected(fileEvent) {
    this.loadCSVFile(fileEvent);
  }


  loadCSVFile(fileEvent) {
    this.csvFilename = '';
    this.csvFileContent = [];
    this.importCSVFileAnalysisResults = [];

    const fileReader = new FileReader();
    fileReader.onload = (content: any) => {
      var records: string[] = content.target.result.split('\n');
      records.forEach(x => {
        var record = x.replace(/\r/g, '')

        if (record.length > 0) {

          var fields = record.split(',').map(field => field.trim());
          this.csvFileContent.push(fields);
        }
      });

      if (this.preCheckCSVContentAnalysis()) {
        this.analyzeFileContent();
        this.readyToImportFileContent = true;
      }

    };

    if (fileEvent.target.files.length > 0) {
      this.analyzingCSVFile = true;
      this.csvFilename = fileEvent.target.files[0].name;
      fileReader.readAsText(fileEvent.target.files[0]);
    }
  }


  preCheckCSVContentAnalysis(): boolean {
    this.analyzingCSVFile = true;

    if (this.csvFileContent.length < 2) {
      this.toastr.error('Error: Can not import. Must have a header row and at least one data row');
      this.importCSVFileAnalysisResults.push("Error: Can not import. Must have a header row and at least one data row.");
      this.analyzingCSVFile = false;
      return false;
    }

    var missingFields = this.validateRequiredFields(this.csvFileContent[0]).join(', ');
    if (missingFields != '') {
      this.toastr.error('Error: First row missing required fields (' + missingFields + ')');
      this.importCSVFileAnalysisResults.push('Error: First row missing required fields (' + missingFields + ')');
      this.analyzingCSVFile = false;
      return false;
    }

    return true;
  }


  validateRequiredFields(headerRecord: string[]): string[] {
    var results: string[] = [];

    this.requiredImportColumns.forEach(s => {
      if (headerRecord.findIndex(x => x.toLowerCase() == s.toLowerCase()) == -1) {
        results.push(s);
      }
    });

    return results;
  }


  analyzeFileContent() {
    var counter: number;
    this.sensorsToImport = [];

    var mappedFields = this.mapFieldColumns(this.csvFileContent[0]);

    for (counter=1; counter < this.csvFileContent.length; counter++) {
      var sensor = this.createOrUpdateSensorWithCSVFileContent(this.csvFileContent[counter], mappedFields);
      this.sensorsToImport.push(sensor);
    };

    this.importCSVFileAnalysisResults.push(`Sensors in file: ${this.sensorsToImport.length}`);
    this.importCSVFileAnalysisResults.push(`New sensors: ${this.sensorsToImport.filter(x => x.isNew).length}`);
    this.importCSVFileAnalysisResults.push(`Existing sensors: ${this.sensorsToImport.filter(x => !x.isNew).length}`);
    this.importCSVFileAnalysisResults.push(`Sensors with GPS start/end dates: ${this.sensorsWithDates}`);
    this.importCSVFileAnalysisResults.push(`Sensors with invalid dates: ${this.sensorsWithInvalidDates}`);
    this.importCSVFileAnalysisResults.push(`Have permission to update: ${this.sensorsToImport.filter(x => x.userFeatureAccess["EditSensor"]).length}`);

    this.analyzingCSVFile = false;
  }


  mapFieldColumns(csvFileLine: string[]): { [key: string] : number } {
    var headerDictionary: { [key: string] : number } = {};

    this.validImportColumns.forEach(x => {
      var index = csvFileLine.findIndex(y => y.toLowerCase() == x.toLowerCase());

      headerDictionary[x.toLowerCase()] =  index;
    });

    return headerDictionary;
  }


  createOrUpdateSensorWithCSVFileContent(csvFileLine: string[], mappedFields: { [key: string] : number } ): Sensor {
    var sensor: Sensor;

    var existingSensor = this.allSensors.find(x => x.uuid.toLowerCase() == csvFileLine[mappedFields["uuid"]].toLowerCase());
    
    if (existingSensor != undefined) {
      sensor = JSON.parse(JSON.stringify(existingSensor));
    }
    else {
      sensor = this.newSensor(csvFileLine[mappedFields["uuid"]].toLowerCase());
    }

    if (csvFileLine[mappedFields["usergroup"]] != undefined && csvFileLine[mappedFields["usergroup"]] != '' && (sensor.userFeatureAccess["EditSensor"] || sensor.isNew)) {
      var selectedUserGroup = this.userGroups.find(x => x.name.toLowerCase() == csvFileLine[mappedFields["usergroup"]].toLowerCase() );
      this.updateUserInfoForSensor(sensor, selectedUserGroup, this.username);
    }

    this.auth.mapUserAccessForSensor(sensor, this.globalSwitch, this.username, this.userGroups);

    sensor.name = (csvFileLine[mappedFields["name"]] != undefined) ? csvFileLine[mappedFields["name"]] : sensor.name;
    sensor.type = (csvFileLine[mappedFields["type"]] != undefined) ? csvFileLine[mappedFields["type"]] : sensor.type;
    sensor.notes = (csvFileLine[mappedFields["notes"]] != undefined) ? csvFileLine[mappedFields["notes"]] : sensor.notes;

    sensor.gpsLocation.locationDescription = (csvFileLine[mappedFields["gps_locationdescription"]] != undefined && csvFileLine[mappedFields["gps_locationdescription"]] != '') ? csvFileLine[mappedFields["gps_locationdescription"]] : sensor.gpsLocation.locationDescription;
    sensor.gpsLocation.gpsLat = (csvFileLine[mappedFields["gps_lat"]] != undefined && csvFileLine[mappedFields["gps_lat"]] != '') ? Number(csvFileLine[mappedFields["gps_lat"]]) : sensor.gpsLocation.gpsLat;
    sensor.gpsLocation.gpsLong = (csvFileLine[mappedFields["gps_long"]] != undefined && csvFileLine[mappedFields["gps_long"]] != '') ? Number(csvFileLine[mappedFields["gps_long"]]) : sensor.gpsLocation.gpsLong;

    var startDate = csvFileLine[mappedFields["gps_startdate"]];
    var endDate = csvFileLine[mappedFields["gps_enddate"]];

    sensor.gpsStartDate = undefined;
    sensor.gpsEndDate = undefined;

    if (startDate != undefined && startDate != '' && endDate != undefined && endDate != '') {
      this.sensorsWithDates++;

      if (this.isValidDate(startDate) && this.isValidDate(endDate)) { 
        sensor.gpsStartDate = (csvFileLine[mappedFields["gps_startdate"]] != undefined && csvFileLine[mappedFields["gps_startdate"]] != '') ? moment.utc(csvFileLine[mappedFields["gps_startdate"]]).toDate() : undefined;
        sensor.gpsEndDate = (csvFileLine[mappedFields["gps_enddate"]] != undefined && csvFileLine[mappedFields["gps_enddate"]] != '') ? moment.utc(csvFileLine[mappedFields["gps_enddate"]]).toDate() : undefined; 
      }
      else {
        this.sensorsWithInvalidDates++;
      }
    }

    return sensor;
  }


  isValidDate(stringDate: string) {
    return !isNaN(Date.parse(stringDate));
  }

  
  updateUserInfoForSensor(sensor: Sensor, selectedUserGroup: UserGroup, username: string) {
    if (selectedUserGroup != null && selectedUserGroup.id > 0) {
      sensor.userGroupId = selectedUserGroup.id;
      sensor.userGroup = selectedUserGroup.name;
    } else {
      sensor.userGroupId = null;
      sensor.userGroup = '';
    }

    sensor.displayedOwner = this.getOwnerName(sensor);
    this.auth.mapUserAccessForSensor(sensor, this.globalSwitch, this.username, this.userGroups);
  }


  getOwnerName(sensor: Sensor): string {
    if (sensor.userGroupId != undefined) {
      return sensor.userGroup;
    }
    else if (sensor.userName != undefined) {
      return sensor.userName;
    }
    else {
      return undefined;
    }
  }


  newSensor(uuid: string): Sensor {
    var newSensor: Sensor = {
      id: 0,
      isNew: true,
      uuid: uuid.toLowerCase(),
      sensorReadingTypes: null,
      sensorReadingTypeNames: '',
      checked: true,
      modifiedDate: new Date(),
      userName: this.username,
      userGroupId: null,
      lastReading: {
        sensorId: 0,
        isLoaded: false,
        date: undefined,
        displayColor: '',
        waitingForLoad: false
      },
      gpsLocation: {
        id: 0,
        gpsLong: 0,
        gpsLat: 0,
        gpsAlt: 0,
        locationDescription: ''
      },
      notes: '',
      virtualSensor: undefined,
      sensorCalibrationHistory: null,
    };
    return newSensor;
  }


  importCSVFileContent() {
    this.importingCSVFileContents = true;
    this.importCSVFileAnalysisResults.push('----------');

    this.sensorsToImport.forEach(x => {
      const sensorInfo = this.mapping.mapSensor2SensorInfo(x);

      if (x.isNew) {
        this.onCreate(x, sensorInfo);
      }
      else if (x.userFeatureAccess["EditSensor"]) {
        this.preEditObjectAsJSONString = JSON.stringify(this.allSensors.find(y => y.id == x.id));
        this.onEdit(x, sensorInfo, this.preEditObjectAsJSONString);
      }
    });

    this.importingCSVFileContents = false;
  }

  
  onEdit(sensor: Sensor, sensorInfo: SensorInfo, preEditObjectAsJSONString: string) {
    this._sensorService.updateSensor([sensorInfo]).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.Edited, sensor, this.common.getEditChanges4Sensors(sensor, preEditObjectAsJSONString));
          this.updateSensorReadingsWithGPSInfo(sensor);
          this.toastr.success('Sensor Updated');
        } else {
          this.toastr.warning('Error saving edits. Reload page.');
        }

        this._sensorService.clearCachedVariables();
        return;
      },
      err => {
        this.toastr.error('Error occurred while updating sensor. Reload page.');
      });
  }


  onCreate(sensor: Sensor, sensorInfo: SensorInfo) {
    this._sensorService.createSensor([sensorInfo]).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.New, sensor, '');
          this.toastr.success('Sensor Created');
        } else {
          this.toastr.warning('Error with creating new sensor. Reload page.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while adding sensor. Reload page.');
      });
  }


  createMessageData(msgActionType: MessageActionType, sensor: Sensor, msgNote: string) {
    const sensorShortName = (sensor.uuid.length < 10) ? sensor.uuid : sensor.uuid.substring(0,3) + '...' + sensor.uuid.substring(sensor.uuid.length-6);
    const displayName = (sensor.name != undefined && sensor.name !== '') ? sensor.name : sensorShortName;

    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Sensors,
      actionType: msgActionType,
      appComponentItem: displayName,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: null
    }

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


  updateSensorReadingsWithGPSInfo(sensor: Sensor) {
    if (sensor.gpsStartDate == undefined && sensor.gpsEndDate == undefined) {
      return;
    }

    if ((sensor.gpsStartDate == undefined && sensor.gpsEndDate != undefined) || (sensor.gpsStartDate != undefined && sensor.gpsEndDate == undefined)) {
      this.importCSVFileAnalysisResults.push(`Sensor: ${sensor.uuid} - A GPS start or end date was provided but not both`);
      return;
    }

    if (moment(sensor.gpsEndDate).diff(moment(sensor.gpsStartDate),'hours') < 0) {
      this.importCSVFileAnalysisResults.push(`Sensor: ${sensor.uuid} - GPS start date does not come before the end date`);
      return;
    }

    var updateCriteria: SensorReadingUpdateCriteria = {
      sensorId: sensor.id,
      gpsLocation: sensor.gpsLocation,
      startDate: sensor.gpsStartDate,
      endDate: sensor.gpsEndDate
    }

    this._sensorService.updateSensorReadingsWithNewGpsInfo(updateCriteria).subscribe(
      results => {
        if (results != null) {
          this.importCSVFileAnalysisResults.push(`Sensor: ${sensor.uuid} - ${results} readings updated with GPS info`);
        }
        else {
          this.toastr.warning('Error while updating sensor readings');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while updating GPS locations.');
      });
  }


}